import { useEffect, useState } from "react";
import axios from "axios";
import { API, fmtErr, money } from "../lib/pos";
import { ArrowLeft, Phone, Search, User } from "lucide-react";

/* Manager · Customers CRM — list + detail. Populated from customer draft
   submissions (unique phone). Shows order + draft history per customer. */
export function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // customer_id
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");

  const load = async (query = "") => {
    try {
      const params = query ? { q: query } : {};
      const r = await axios.get(`${API}/customers`, { params });
      setRows(r.data || []);
    } catch (e) { setErr(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    axios.get(`${API}/customers/${selected}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [selected]);

  if (selected && detail) return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <button className="outline" style={{ marginBottom: 8 }} onClick={() => setSelected(null)} data-testid="cust-back">
            <ArrowLeft size={13} style={{ marginRight: 4 }} /> Back to customers
          </button>
          <p className="eyebrow">CUSTOMER</p>
          <h1>{detail.customer.name}</h1>
          <p className="muted"><Phone size={12} style={{ verticalAlign: "middle" }} /> {detail.customer.phone} · since {new Date(detail.customer.created_at).toLocaleDateString()}</p>
        </div>
      </div>
      <div className="metrics" style={{ marginTop: 20 }}>
        <div><span>Drafts placed</span><b>{detail.customer.drafts_count || 0}</b></div>
        <div><span>Orders placed</span><b>{detail.customer.orders_count || 0}</b></div>
        <div><span>Total spent</span><b>{money((detail.orders || []).reduce((a, o) => a + (o.total || 0), 0))}</b></div>
      </div>

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="section-line"><h2>Recent orders</h2><span className="muted">{detail.orders.length}</span></div>
        {detail.orders.length === 0
          ? <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>No orders yet.</p>
          : <div className="report-table" style={{ marginTop: 10 }}>
              <div className="rt-head" style={{ gridTemplateColumns: "80px 1fr 80px 100px 120px" }}>
                <span>Order</span><span>When</span><span>Token</span><span>Status</span><span>Total</span>
              </div>
              {detail.orders.slice(0, 40).map((o) => (
                <div className="rt-row" key={o.id} style={{ gridTemplateColumns: "80px 1fr 80px 100px 120px" }} data-testid={`cust-order-${o.id}`}>
                  <b>#{o.order_number}</b>
                  <span>{new Date(o.created_at).toLocaleString()}</span>
                  <em>T{String(o.token_number || 0).padStart(2, "0")}</em>
                  <span>{o.status}</span>
                  <b>{money(o.total || 0)}</b>
                </div>
              ))}
            </div>}
      </div>

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="section-line"><h2>Recent drafts</h2><span className="muted">{detail.drafts.length}</span></div>
        {detail.drafts.length === 0
          ? <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>No drafts.</p>
          : <div className="report-table" style={{ marginTop: 10 }}>
              <div className="rt-head" style={{ gridTemplateColumns: "80px 1fr 100px 120px" }}>
                <span>Draft</span><span>When</span><span>Status</span><span>Subtotal</span>
              </div>
              {detail.drafts.slice(0, 40).map((d) => (
                <div className="rt-row" key={d.id} style={{ gridTemplateColumns: "80px 1fr 100px 120px" }} data-testid={`cust-draft-${d.draft_number}`}>
                  <b>#{d.draft_number}</b>
                  <span>{new Date(d.created_at).toLocaleString()}</span>
                  <em>{d.status}</em>
                  <b>{money(d.subtotal || 0)}</b>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">CRM</p>
          <h1>Customers</h1>
          <p className="muted">Everyone who has placed a draft. Phone numbers are unique.</p>
        </div>
      </div>
      {err && <div className="auth-error">{err}</div>}
      <div className="report-filters" style={{ marginTop: 6 }}>
        <label className="field" style={{ flex: 1 }}>
          <span><Search size={11} style={{ verticalAlign: "middle" }} /> Search name or phone</span>
          <input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rahul or 98765…" />
        </label>
      </div>
      <div className="report-table" style={{ marginTop: 14 }}>
        <div className="rt-head" style={{ gridTemplateColumns: "1fr 140px 90px 90px 160px" }}>
          <span>Name</span><span>Phone</span><span>Drafts</span><span>Orders</span><span>Last seen</span>
        </div>
        {rows.length === 0 && <div className="empty" style={{ padding: 22 }}><b>No customers yet</b></div>}
        {rows.map((c) => (
          <div
            className="rt-row"
            key={c.id}
            style={{ gridTemplateColumns: "1fr 140px 90px 90px 160px", cursor: "pointer" }}
            data-testid={`cust-row-${c.id}`}
            onClick={() => setSelected(c.id)}
          >
            <b><User size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />{c.name}</b>
            <span>{c.phone}</span>
            <em>{c.drafts_count || 0}</em>
            <em>{c.orders_count || 0}</em>
            <span>{c.last_seen ? new Date(c.last_seen).toLocaleString() : "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
